import { useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";

import styled from "styled-components/macro";

import { useNewGivingUI } from "Flocknote/apiHooks";

import givingKeys from "Giving/API/keys";
import { useFundList } from "Giving/API/queryHooks";
import LinkListRow, {
  LinkListRowsWrapper,
} from "NavBar/components/LinkListRow";
import { menuTypographyProps } from "NavBar/components/NavLink";
import { navSectionKeys } from "NavBar/navBarStore";

import ColorSquare from "UI/FnrColorSquare";
import FnrScaleIt from "UI/FnrScaleIt";
import FnrTypography from "UI/FnrTypography";

import Amount from "./Amount";

const getFundUrl = (hideLegacyGivingUI, fundID) =>
  hideLegacyGivingUI ? `giving/funds/${fundID}` : `giving?fund=${fundID}`;

export const sortFundsWithSubfunds = (funds) => {
  const childrenByParent = new Map();
  const parents = [];
  for (const fund of funds) {
    if (fund.parentFundID) {
      const arr = childrenByParent.get(fund.parentFundID) ?? [];
      arr.push(fund);
      childrenByParent.set(fund.parentFundID, arr);
    } else {
      parents.push(fund);
    }
  }
  return parents.flatMap((p) => [
    p,
    ...(childrenByParent.get(p.fundID) ?? []),
  ]);
};

export const buildNavFundsList = (funds) => {
  if (!funds.some((fund) => fund.isFavorite)) {
    return sortFundsWithSubfunds(funds);
  }

  const childrenByParent = new Map();
  const parents = [];
  for (const fund of funds) {
    if (fund.parentFundID) {
      const arr = childrenByParent.get(fund.parentFundID) ?? [];
      arr.push(fund);
      childrenByParent.set(fund.parentFundID, arr);
    } else {
      parents.push(fund);
    }
  }

  return parents.flatMap((parent) => {
    const favoriteChildren = (childrenByParent.get(parent.fundID) ?? []).filter(
      (child) => child.isFavorite
    );
    if (!parent.isFavorite && favoriteChildren.length === 0) return [];
    return [parent, ...favoriteChildren];
  });
};

const Funds = () => {
  const { hideLegacyGivingUI } = useNewGivingUI();
  const funds = useFundList();

  const queryClient = useQueryClient();

  useEffect(
    // see www/assets/networkGiving/networkGiving.js for dispatches
    function listenForFundsSummaryInvalidationFromLegacyJs() {
      const invalidateFundsSummary = () =>
        queryClient.invalidateQueries({
          queryKey: givingKeys.getFundList(),
        });

      window.addEventListener(
        "invalidate-funds-summary",
        invalidateFundsSummary
      );
      return () =>
        window.removeEventListener(
          "invalidate-funds-summary",
          invalidateFundsSummary
        );
    },
    [queryClient]
  );

  return (
    <LinkListRowsWrapper>
      {buildNavFundsList(funds).map(
        ({ fundID, fundName, totalAmount, colorCode, parentFundID }) => {
          const to = getFundUrl(hideLegacyGivingUI, fundID);
          const isSubfund = !!parentFundID;

          return (
            <LinkListRow
              data-cy={`NavBar_Fund${fundID}`}
              key={fundID}
              to={to}
              navSectionKey={navSectionKeys.giving}
              omitNumericIndicator
            >
              <ColorAndNameWrapper
                $isSubfund={isSubfund}
                data-issubfund={isSubfund || undefined}
              >
                <FnrScaleIt scale={0.8}>
                  <ColorSquare
                    circle
                    selected
                    backgroundColor={colorCode}
                    size="sm"
                  />
                </FnrScaleIt>
                <FnrTypography {...menuTypographyProps} noWrap={false}>
                  {fundName}
                </FnrTypography>
              </ColorAndNameWrapper>
              <Amount amount={totalAmount?.whole} />
            </LinkListRow>
          );
        }
      )}
    </LinkListRowsWrapper>
  );
};

const ColorAndNameWrapper = styled.div`
  display: flex;
  gap: 8px;
  align-items: center;
  ${({ $isSubfund }) => $isSubfund && "margin-left: 16px;"}
`;

export default Funds;
